<script setup lang="ts">
import { computed, ref, toRef, watch } from 'vue';
import BaseGrid from '@/common/components/molecules/grid/BaseGrid.vue';
import TextViewerWindow from '@/common/components/organisms/textViewerWindow/TextViewerWindow.vue';
import { listViewGridOption } from '@/common/define/grid.define';
import { useInternational } from '@/common/locale';
import { findCustomColumnIndexMap, makeRows } from '@/common/utils/gridUtils';
import { CustomColumn } from '@/common/utils/types';
import { getFilterItemsByGridColumns } from '@/common/components/molecules/filterSearch/filterSearch.utils';
import { useGetV7InfraHostsProcessByHostId } from '@/api-client/infra/queries/infra-host-v7-controller/infra-host-v7-controller';
import { InfraHostProcessItem } from '@/api-client/infra/model';
import { FRAME_NAMES } from '@/common/define/apiTrace.define';
import { USER_ENV_KEYS } from '@/common/define/userEnv.define';
import { useVueQueryErrorHandler } from '@/common/composables/useVueQueryErrorHandler';

interface Props {
  isShow?: boolean;
  hostId?: string;
}
const props = withDefaults(defineProps<Props>(), {
  isShow: false,
  hostId: '',
});

const { t } = useInternational();
const { errorHandlerWithMessage } = useVueQueryErrorHandler();

// 호스트 상세 프로세스 탭과 동일 데이터. 단, CPU/Virtual Memory/Real Memory 컬럼 제외, Args는 클릭 가능.
const columns: (CustomColumn & { field: keyof InfraHostProcessItem })[] = [
  { caption: 'PID', field: 'pid', type: 'stringNumber', width: 80, searchable: true },
  { caption: 'PPID', field: 'ppid', type: 'stringNumber', width: 80, searchable: true },
  {
    caption: 'Process Name',
    field: 'processName',
    type: 'string',
    width: 200,
    searchable: true,
    defaultSearchField: true,
  },
  { caption: 'Args', field: 'args', type: 'string', width: 320, searchable: true, clickable: true },
  { caption: 'User Name', field: 'userName', type: 'string', width: 140, searchable: true },
  { caption: 'Start Time', field: 'startTime', type: 'string', width: 160, searchable: true },
];
const idxMap = findCustomColumnIndexMap({ columns });

const gridSearchBarOption = {
  use: true,
  useMergedToolbar: true,
  filterSearch: {
    use: true,
    storageKey: USER_ENV_KEYS.FILTERSEARCH_alert_eventAlertDetail_hostProcessList,
    filterItems: getFilterItemsByGridColumns(columns),
  },
};

const hostId = toRef(props, 'hostId');
const rows = ref<(string | number)[][]>([]);

// 실시간 갱신 없이 탭 활성(노출 + hostId 존재) 시 1회 조회
const isQueryEnabled = computed(() => !!props.hostId && !!props.isShow);

const { data: queryData } = useGetV7InfraHostsProcessByHostId(hostId, {
  request: {
    frameName: FRAME_NAMES.HOST_DETAIL.PROCESS,
  },
  query: {
    enabled: isQueryEnabled,
    meta: {
      onError: (error: unknown) => errorHandlerWithMessage({ error }),
    },
  },
});

watch(
  queryData,
  (data) => {
    const list = data?.data?.data ?? [];
    rows.value = makeRows(list, columns).sort(
      (a, b) => Number(a[idxMap.pid]) - Number(b[idxMap.pid]),
    );
  },
  { immediate: true },
);

// Args 전체 보기 모달
const isShowArgsModal = ref(false);
const argsText = ref('');
const onClickCell = ({ field, value }: { field: string; value: unknown }) => {
  if (field !== 'args') {
    return;
  }
  argsText.value = `${value ?? ''}`;
  isShowArgsModal.value = true;
};
</script>

<template>
  <div class="alert-host-process-list">
    <base-grid
      :columns="columns"
      :rows="rows"
      :option="{ ...listViewGridOption, page: { use: false } }"
      :export-excel-option="{ title: t('WORD.PROCESS_LIST') }"
      :search-bar-option="gridSearchBarOption"
      @click-cell="onClickCell"
    />
    <text-viewer-window
      v-model:is-show="isShowArgsModal"
      title="Args"
      :text="argsText"
      :show-message-title="false"
      :line-number="false"
    />
  </div>
</template>

<style lang="scss" scoped>
.alert-host-process-list {
  height: 100%;
}
</style>
